import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "./Navbar";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1707836885254-79b6e3d7b18d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHx3ZWJzaXRlJTIwbW9ja3VwfGVufDB8fHx8MTc4ODI3NzgxN3ww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 10);
    rx.set(-py * 10);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      onMouseLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
      style={{ perspective: 1200 }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mono-label text-[#E05638] mb-6"
          >
            Web as a Service · Studio Creativo
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-6xl lg:text-7xl"
          >
            <span className="mask-line">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                Creiamo il tuo sito.
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
                Lo gestiamo <span className="text-[#E05638]">ogni mese.</span>
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                Tu pensa al business.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-neutral-600 leading-relaxed"
          >
            Siti web su misura con design editoriale, manutenzione continua e gestione SEO in
            abbonamento. Niente costi astronomici una tantum: solo risultati, mese dopo mese.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-plans-button"
              onClick={() => scrollToSection("piani")}
              className="inline-flex items-center gap-2 rounded-full bg-[#111111] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
            >
              Scopri i piani
              <ArrowRight size={16} />
            </button>
            <button
              data-testid="hero-cta-demo-button"
              onClick={() => scrollToSection("portfolio")}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-7 py-3.5 text-sm font-semibold text-neutral-800 hover:border-[#111111] hover:bg-[#111111] hover:text-white transition-colors duration-300"
            >
              Guarda i lavori
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" ref={ref}>
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
            className="relative rounded-2xl bg-white border border-neutral-200 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.18)] overflow-hidden"
          >
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-neutral-100 bg-neutral-50">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E05638]" />
              <span className="w-2.5 h-2.5 rounded-full bg-neutral-300" />
              <span className="w-2.5 h-2.5 rounded-full bg-neutral-300" />
              <span className="ml-3 mono-label text-neutral-400">iltuosito.it</span>
            </div>
            <img src={HERO_IMG} alt="Mockup sito web realizzato da Lumora" className="w-full h-72 sm:h-80 object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
